import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, BookOpen, ChevronRight, Trophy, Target, Languages, Calendar } from 'lucide-react';
import api from '../utils/api';
import { useI18n } from '../contexts/I18nContext';

const BibleCourses = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [books, setBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);

  // Map course IDs to i18n keys for translated content
  const getCourseTranslations = (courseId) => {
    const translations = {
      'biblical_hebrew': {
        name: t('bible.biblicalHebrew'),
        description: t('bible.studyOldTestament'),
        testament: t('bible.oldTestament')
      },
      'biblical_greek': {
        name: t('bible.biblicalGreek'),
        description: t('bible.studyNewTestament'),
        testament: t('bible.newTestament')
      },
      'greek_nt': {
        name: t('bible.biblicalGreek'),
        description: t('bible.studyNewTestament'),
        testament: t('bible.newTestament')
      }
    };
    return translations[courseId] || {};
  };

  // Get translated book name
  const getBookName = (bookId) => {
    // Try to get translation, fallback to book ID with first letter capitalized
    const key = `bible.books.${bookId}`;
    const translated = t(key);
    // If translation key is returned as-is, it means no translation exists
    if (translated === key || translated.startsWith('bible.books.')) {
      // Fallback: capitalize first letter of each word
      return bookId.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
    return translated;
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bible/courses');
      setCourses(response.data.courses);
    } catch (err) {
      setError('Failed to load Bible courses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectCourse = useCallback(async (course) => {
    if (selectedCourse?.course_id === course.course_id) {
      setSelectedCourse(null);
      setBooks([]);
      return;
    }
    
    try {
      setLoadingBooks(true);
      setSelectedCourse(course);
      const response = await api.get(`/bible/courses/${course.course_id}/books`);
      setBooks(response.data.books);
    } catch (err) {
      console.error('Failed to load books:', err);
    } finally {
      setLoadingBooks(false);
    }
  }, [selectedCourse]);

  const openBook = (courseId, bookId) => {
    navigate(`/bible/${courseId}/${bookId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="w-10 h-10 text-amber-500" />
            <h1 className="text-4xl font-bold text-white">{t('bible.title')}</h1>
          </div>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('bible.subtitle')}
          </p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6 text-red-400">
            {error}
          </div>
        )}

        {/* Course Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {courses.map((course) => {
            const courseI18n = getCourseTranslations(course.course_id);
            return (
            <div
              key={course.course_id}
              data-testid={`course-card-${course.course_id}`}
              className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border transition-all duration-300 cursor-pointer
                ${selectedCourse?.course_id === course.course_id 
                  ? 'border-amber-500 shadow-lg shadow-amber-500/20' 
                  : 'border-slate-700 hover:border-slate-600'}`}
              onClick={() => selectCourse(course)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center 
                      ${course.course_id === 'biblical_hebrew' 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : 'bg-purple-500/20 text-purple-400'}`}>
                      <Languages className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-white">{courseI18n.name || course.name}</h2>
                      <p className={`text-lg ${course.direction === 'rtl' ? 'font-hebrew' : ''}`} dir={course.direction}>
                        {course.native_name}
                      </p>
                    </div>
                  </div>
                  <span className={`fi fi-${course.flag} text-2xl`}></span>
                </div>

                <p className="text-slate-400 mb-4">{courseI18n.description || course.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Book className="w-4 h-4" />
                      <span>{t('bible.booksLabel')}</span>
                    </div>
                    <p className="text-white font-semibold">{course.book_count}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Target className="w-4 h-4" />
                      <span>{t('bible.chapters')}</span>
                    </div>
                    <p className="text-white font-semibold">{course.total_chapters}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-400">{t('bible.progress')}</span>
                    <span className="text-amber-400">{course.progress_percent}%</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
                      style={{ width: `${course.progress_percent}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {course.completed_chapters} / {course.total_chapters} {t('bible.completed')}
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-300">
                    {courseI18n.testament || course.testament}
                  </span>
                  <button 
                    data-testid={`start-course-${course.course_id}`}
                    className="flex items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectCourse(course);
                    }}
                  >
                    <span>{t('bible.viewBooks')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
          })}
        </div>

        {/* Books List */}
        {selectedCourse && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6" data-testid="books-list">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Book className="w-5 h-5 text-amber-500" />
              {getCourseTranslations(selectedCourse.course_id).name || selectedCourse.name} - {t('bible.booksLabel')}
            </h3>

            {loadingBooks ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {books.map((book) => (
                  <div
                    key={book.id}
                    data-testid={`book-card-${book.id}`}
                    className="bg-slate-900/50 rounded-lg p-4 hover:bg-slate-900/70 transition-colors cursor-pointer border border-transparent hover:border-amber-500/30"
                    onClick={() => openBook(selectedCourse.course_id, book.id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-white">{getBookName(book.id)}</h4>
                      <span className="text-xs text-slate-500">{book.chapters} {t('bible.ch')}</span>
                    </div>
                    {selectedCourse.course_id === 'biblical_hebrew' && (
                      <p className="text-sm text-slate-400 font-hebrew" dir="rtl">{book.hebrew_name}</p>
                    )}
                    {selectedCourse.course_id === 'biblical_greek' && book.greek_name && (
                      <p className="text-sm text-slate-400">{book.greek_name}</p>
                    )}
                    
                    {/* Book Progress */}
                    <div className="mt-2">
                      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-amber-500 rounded-full"
                          style={{ width: `${book.progress_percent}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {book.completed_chapters}/{book.chapters} {t('bible.completed')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
            <Trophy className="w-8 h-8 text-amber-500 mb-3" />
            <h4 className="text-lg font-semibold text-white mb-2">{t('bible.trackProgress')}</h4>
            <p className="text-slate-400 text-sm">
              {t('bible.trackProgressDesc')}
            </p>
          </div>
          <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
            <Calendar className="w-8 h-8 text-amber-500 mb-3" />
            <h4 className="text-lg font-semibold text-white mb-2">{t('bible.studyModes')}</h4>
            <p className="text-slate-400 text-sm">
              {t('bible.studyModesDesc')}
            </p>
          </div>
          <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
            <Languages className="w-8 h-8 text-amber-500 mb-3" />
            <h4 className="text-lg font-semibold text-white mb-2">{t('bible.originalLanguages')}</h4>
            <p className="text-slate-400 text-sm">
              {t('bible.originalLanguagesDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BibleCourses;
